import { getFileNameOnly } from "@components/fileUploader/File.utils";
import { IFieldInfoProperties } from "@components/smart/FieldInfo";
import BindingContext, { IEntity } from "@odata/BindingContext";
import { isDefined } from "@utils/general";
import { TFunction } from "i18next";

import { BasicInputSizes, FieldType, LabelStatus, TextAlign } from "../../enums";
import { FormStorage } from "../formView/FormStorage";

export const APPLY_ISDOC_ACTION = "applyIsDoc";
export const APPLY_ROSSUM_ACTION = "applyRossum";

export const getFiles = (formStorage: FormStorage): IEntity[] => {
    const formDefinition = formStorage?.data?.definition;
    if (!formDefinition?.files || !formStorage.data.bindingContext) {
        return [];
    }
    const { property, singlePreview, collection } = formDefinition.files;

    if (singlePreview) {
        const bc = formStorage.data.bindingContext.navigate(property);
        const fileMetadata = formStorage.getValue(bc);
        // mitigate behavior of file attachments, which return IDocumentAttachment entity with FileMetadata in "property" prop.
        return fileMetadata?.Id ? [{ [property]: fileMetadata }] : [];
    }

    const filesBc = formStorage.data.bindingContext.navigate(collection);

    return formStorage.getValue(filesBc);
};

export function hasFormAttachments(formStorage: FormStorage): boolean {
    if (!formStorage?.data?.bindingContext) {
        return false;
    }
    const readOnlyAttachmentPaths = formStorage.data.definition?.files?.readOnlyAttachmentPaths;
    if (readOnlyAttachmentPaths?.length > 0) {
        return readOnlyAttachmentPaths.some((path) => {
            const bc = formStorage.data.bindingContext.navigate(path);
            const attachments = formStorage.getValue(bc);
            return attachments?.length > 0;
        });
    }
    return false;
}

export const getFileNameWriteLineDef = (t: TFunction): IFieldInfoProperties => ({
    type: FieldType.WriteLine,
    width: BasicInputSizes.L,
    labelStatus: LabelStatus.Removed,
    textAlign: TextAlign.Left,
    isConfirmable: true,
    isRequired: false,
    fieldSettings: {
        isExtending: true,
        formatter: getFileNameOnly,
        placeholder: t("Components:FileUploader.Name")
    }
});

export const ATTACHMENT_LOCAL_BC_PATH = BindingContext.localContext("bc");

export function getAllAttachmentsFromPaths(readOnlyAttachmentPaths: string[], storage: FormStorage): IEntity[] {
    const items: IEntity[] = [];

    function _pushItems(val: IEntity[] | IEntity, bc: BindingContext): void {
        if (Array.isArray(val)) {
            if (val?.length > 0) {
                items.push(...val.map(v => ({
                    ...v,
                    [ATTACHMENT_LOCAL_BC_PATH]: bc.addKey(v.Id)
                })));
            }
        } else {
            if (isDefined(val)) {
                items.push({
                    ...val,
                    [ATTACHMENT_LOCAL_BC_PATH]: bc
                });
            }
        }
    }

    readOnlyAttachmentPaths?.forEach((path) => {
        const bc = storage.data.bindingContext.navigate(path);
        if (bc.getParent().isAnyPartCollection()) {
            for (const item of bc.getParentCollection().getParent().iterateNavigation(bc.getParentCollection().getPath(), storage.getValue(bc.getParentCollection()))) {
                _pushItems(item.entity[bc.getPath()], item.bindingContext.navigate(bc.getPath()));
            }
        } else {
            _pushItems(storage.getValue(bc), bc);
        }
    });
    return items;
}